import React, { useState, useEffect, useCallback } from "react";
import {
  Grid,
  CardHeader,
  CardContent,
  Box,
  TextField,
  MenuItem,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import { Helmet } from "react-helmet-async";
import { Form, FormikProvider, useFormik } from "formik";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import CardLayout from "../../layouts/CardLayout";
import errorHandler from "../../utils/errorHandler";
import { AdminSchema } from "../../utils/Schema";
import { adminServices } from "../../services/AllServices";
import { ADMIN_ROLES } from "../../utils/constants";

function AddEditAdmin() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const fetchAdminData = useCallback(async () => {
    if (id) {
      setLoading(true);
      try {
        const response = await adminServices.getById({ id });
        setData(response?.data?.data);
      } catch (error) {
        errorHandler(error);
      } finally {
        setLoading(false);
      }
    }
  }, [id]);

  useEffect(() => {
    fetchAdminData();
  }, [fetchAdminData]);

  const formik = useFormik({
    enableReinitialize: true,
    validationSchema: AdminSchema,
    initialValues: {
      id: id || "",
      name: data?.name || "",
      email: data?.email || "",
      password: "",
      role: data?.role || "",
    },
    onSubmit: async (values) => {
      setLoading(true);
      try {
        const payload = {
          ...values,
          id: id || undefined,
        };

        const response = id
          ? await adminServices.edit(payload)
          : await adminServices.add(payload);
        
        toast.success(response?.data?.message);
        navigate("/admin-management");
      } catch (error) {
        errorHandler(error);
      } finally {
        setLoading(false);
      }
    },
  });

  const { values, touched, errors, handleChange, handleSubmit } = formik;

  return (
    <Box>
      <Helmet>
        <title>{id ? "Edit" : "Add"} Admin</title>
      </Helmet>
      <CardLayout>
        <CardHeader title={`${id ? "Edit" : "Add"} Admin`} />
        <CardContent>
          <FormikProvider value={formik}>
            <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Name"
                    required
                    name="name"
                    value={values.name}
                    onChange={handleChange}
                    error={Boolean(touched.name && errors.name)}
                    helperText={touched.name && errors.name}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Email"
                    required
                    name="email"
                    value={values.email}
                    onChange={handleChange}
                    error={Boolean(touched.email && errors.email)}
                    helperText={touched.email && errors.email}
                  />
                </Grid>

                {!id && (
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Password"
                      required
                      type="password"
                      name="password"
                      value={values.password}
                      onChange={handleChange}
                      error={Boolean(touched.password && errors.password)}
                      helperText={touched.password && errors.password}
                    />
                  </Grid>
                )}

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    select
                    label="Role"
                    required
                    name="role"
                    value={values.role}
                    onChange={handleChange}
                    error={Boolean(touched.role && errors.role)}
                    helperText={touched.role && errors.role}
                  >
                    <MenuItem value={ADMIN_ROLES.MGT}>Management (Mgt)</MenuItem>
                    <MenuItem value={ADMIN_ROLES.JUNIOR}>Junior Admin</MenuItem>
                  </TextField>
                </Grid>

                <Grid item xs={12}>
                  <Box mt={3}>
                    <LoadingButton
                      fullWidth
                      size="large"
                      type="submit"
                      variant="contained"
                      loading={loading}
                    >
                      {id ? "Update" : "Create"} Admin
                    </LoadingButton>
                  </Box>
                </Grid>
              </Grid>
            </Form>
          </FormikProvider>
        </CardContent>
      </CardLayout>
    </Box>
  );
}

export default AddEditAdmin;
